import { slugify } from './stringUtils';

export const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isBlankString = (value: unknown): value is string => {
  return typeof value === 'string' && !value.trim();
};

export const isNullishOrBlank = (value: unknown): boolean => {
  return value === null || typeof value === 'undefined' || value === '' || isBlankString(value);
};

export type ConfigurationFormPageRecord = Record<string, unknown>;

type ParseJsonStrictResult = { ok: true; value: unknown } | { ok: false; error: string };

export const parseJsonStrict = (value: unknown, label: string): ParseJsonStrictResult => {
  if (typeof value !== 'string') {
    return { ok: true, value };
  }

  if (!value.trim()) {
    return { ok: true, value: '' };
  }

  try {
    return { ok: true, value: JSON.parse(value) };
  } catch {
    return { ok: false, error: `${label} must be valid JSON.` };
  }
};

export const coerceBooleanFieldValue = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }
  if (value === 1) {
    return true;
  }
  if (value === 0) {
    return false;
  }
  return fallback;
};

type ParsedConfigurationPagesResult = {
  pages: ConfigurationFormPageRecord[];
  parseError: string | null;
};

export const parseConfigurationFormPages = (configurationForm: unknown): ParsedConfigurationPagesResult => {
  if (configurationForm === null || configurationForm === undefined || configurationForm === '') {
    return { pages: [], parseError: null };
  }

  const parsedForm = parseJsonStrict(configurationForm, 'Configuration form');
  if (!parsedForm.ok) {
    return { pages: [], parseError: parsedForm.error };
  }

  if (Array.isArray(parsedForm.value)) {
    const pages = parsedForm.value.filter((page): page is ConfigurationFormPageRecord => isRecord(page));
    if (pages.length !== parsedForm.value.length) {
      return { pages: [], parseError: 'Configuration form pages must be JSON objects.' };
    }
    return {
      pages,
      parseError: null,
    };
  }

  if (isRecord(parsedForm.value)) {
    const rawPages = Object.values(parsedForm.value);
    const pages = rawPages.filter((page): page is ConfigurationFormPageRecord => isRecord(page));
    if (pages.length !== rawPages.length) {
      return { pages: [], parseError: 'Configuration form pages must be JSON objects.' };
    }
    return {
      pages,
      parseError: null,
    };
  }

  return { pages: [], parseError: 'Configuration form must be a JSON array or object.' };
};

export const extractConfigurationPageKey = (page: Record<string, unknown>, pageIndex: number): string => {
  const title = typeof page.title === 'string' && page.title.trim().length > 0 ? page.title : `Page ${pageIndex + 1}`;
  return slugify(title) || `page-${pageIndex + 1}`;
};
